import { ofType } from 'redux-observable';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, startWith } from 'rxjs/operators';
import { ActionCreators, requestActions } from '../apiHelpers';
import { ID, ReduxAction } from '../../types';
import { apiObservable } from '@support/api';
import { ViewContactPermission } from '@reducers/data/shareStatuses/contacts';
import { Sharee } from '@models/Sharee';
import { ShareStatus } from '@models/ShareStatus';
import { ViewGroupPermission } from '@reducers/data/shareStatuses/groups';

type SharedResourceType = 'CONTACT' | 'GROUP';
const viewPermissionForSharedResourceType: Record<SharedResourceType, string> = {
  CONTACT: ViewContactPermission,
  GROUP: ViewGroupPermission, // import from the reducer or wherever this is defined
};

interface SharingEpicActionCreators {
  shareeActions: ActionCreators;
  verifiedShareePermissionActions: ActionCreators;
  waitingShareePermissionActions: ActionCreators;
  publicSharingActions: ActionCreators;
}

interface SharingActionCreators<P> {
  fetchShareStatus: (sharedResourceID: ID) => ReduxAction;
  shareWithEmail: (payload: { sharedResourceID: ID; email: string }) => ReduxAction;
  unshareWithSharee: (payload: { sharedResourceID: ID; sharee: Sharee<P> }) => ReduxAction;
  addPermissionForSharee: (payload: {
    sharedResourceID: ID;
    sharee: Sharee<P>;
    permission: P;
  }) => ReduxAction;
  removePermissionForSharee: (payload: {
    sharedResourceID: ID;
    sharee: Sharee<P>;
    permission: P;
  }) => ReduxAction;
  sharePublicly: (shareStatus: ShareStatus<P>) => ReduxAction;
  unsharePublicly: (shareStatus: ShareStatus<P>) => ReduxAction;
  addPublicPermission: (shareStatus: ShareStatus<P>, permission: P) => ReduxAction;
  removePublicPermission: (shareStatus: ShareStatus<P>, permission: P) => ReduxAction;
}

/* Action creators */
export const createSharingEpicActionCreators = (
  sharedResourceType: SharedResourceType,
): SharingEpicActionCreators => {
  return {
    shareeActions: requestActions(`${sharedResourceType}_SHAREE`, ['fetchAll', 'create']),
    verifiedShareePermissionActions: requestActions(
      `VERIFIED_${sharedResourceType}_SHAREE_PERMISSION`,
      ['delete'],
    ),
    waitingShareePermissionActions: requestActions(
      `WAITING_${sharedResourceType}_SHAREE_PERMISSION`,
      ['delete'],
    ),
    publicSharingActions: requestActions(`PUBLIC_${sharedResourceType}_SHARING`, ['update']),
  };
};

/* Component actions */
type RemovePermissionActionCreator<P> = (props: {
  sharedResourceID: ID;
  sharee: Sharee<P>;
  permissionID: ID;
  removeAllAccess?: boolean;
}) => ReduxAction;
type UnshareActionCreator<P> = (sharedResourceID: ID, sharee: Sharee<P>) => ReduxAction;

function createPermissionRemovalActions<P>(
  actionCreators: ActionCreators,
): [RemovePermissionActionCreator<P>, UnshareActionCreator<P>] {
  const removePermission = ({ sharedResourceID, sharee, permissionID, removeAllAccess }) =>
    actionCreators['delete']({ sharedResourceID, sharee, permissionID, removeAllAccess });
  const unshare = (sharedResourceID: ID, sharee: Sharee<P>) =>
    removePermission({
      sharedResourceID,
      sharee,
      permissionID: sharee.idForMainPermission,
      removeAllAccess: true,
    });
  return [removePermission, unshare];
}

export function createSharingActions<P>(
  sharedResourceType: SharedResourceType,
  actionCreators: SharingEpicActionCreators,
): SharingActionCreators<P> {
  const addPermissionForEmail = ({ sharedResourceID, email, permission }) =>
    actionCreators.shareeActions['create']({ sharedResourceID, email, permission });
  const [removePermissionForVerifiedEmail, unshareWithVerifiedEmail] =
    createPermissionRemovalActions(actionCreators.verifiedShareePermissionActions);
  const [removePermissionForWaitingEmail, unshareWithWaitingEmail] = createPermissionRemovalActions(
    actionCreators.waitingShareePermissionActions,
  );

  return {
    fetchShareStatus: (sharedResourceID) => ({
      type: `FETCH_${sharedResourceType}_SHARE_STATUS`,
      payload: { sharedResourceID },
    }),
    shareWithEmail: ({ sharedResourceID, email }) =>
      addPermissionForEmail({
        sharedResourceID,
        email,
        permission: viewPermissionForSharedResourceType[sharedResourceType] as P,
      }),
    unshareWithSharee: ({ sharedResourceID, sharee }) => {
      if (sharee.shareType == 'verified') {
        return unshareWithVerifiedEmail(sharedResourceID, sharee);
      } else {
        return unshareWithWaitingEmail(sharedResourceID, sharee);
      }
    },
    addPermissionForSharee: ({ sharedResourceID, sharee, permission }) =>
      addPermissionForEmail({
        sharedResourceID,
        email: sharee.email,
        permission,
      }),
    removePermissionForSharee: ({ sharedResourceID, sharee, permission }) => {
      const permissionID = sharee.idForPermission(permission);
      if (sharee.shareType == 'verified') {
        return removePermissionForVerifiedEmail({ sharedResourceID, sharee, permissionID });
      } else {
        return removePermissionForWaitingEmail({ sharedResourceID, sharee, permissionID });
      }
    },
    sharePublicly: (shareStatus) =>
      actionCreators.publicSharingActions['update']({ shareStatus: shareStatus.sharePublicly() }),
    unsharePublicly: (shareStatus) =>
      actionCreators.publicSharingActions['update']({ shareStatus: shareStatus.unsharePublicly() }),
    addPublicPermission: (shareStatus, permission) =>
      actionCreators.publicSharingActions['update']({
        shareStatus: shareStatus.addPublicPermission(permission),
      }),
    removePublicPermission: (shareStatus, permission) =>
      actionCreators.publicSharingActions['update']({
        shareStatus: shareStatus.removePublicPermission(permission),
      }),
  };
}

const apiPathForSharedResourceType: Record<SharedResourceType, string> = {
  CONTACT: 'contacts',
  GROUP: 'group',
};

export function createSharingEpics(
  sharedResourceType: SharedResourceType,
  actionCreators: SharingEpicActionCreators,
): ((action$: Observable<ReduxAction>) => Observable<ReduxAction>)[] {
  const resourcePath = apiPathForSharedResourceType[sharedResourceType];
  const fetchShareStatusEpic = (action$: Observable<ReduxAction>) =>
    action$.pipe(
      ofType(`FETCH_${sharedResourceType}_SHARE_STATUS`),
      mergeMap(({ payload }) =>
        apiObservable('GET', `/network/${resourcePath}/${payload.sharedResourceID}/share/`).pipe(
          map(({ response }) =>
            actionCreators.shareeActions['fetchAllSucceeded']({
              ...response,
              sharedResourceID: payload.sharedResourceID,
            }),
          ),
          startWith(
            actionCreators.shareeActions['fetchAllRequested']({
              sharedResourceID: payload.sharedResourceID,
            }),
          ),
          catchError((err) =>
            of(
              actionCreators.shareeActions['fetchAllFailed']({
                ...err,
                sharedResourceID: payload.sharedResourceID,
              }),
            ),
          ),
        ),
      ),
    );

  const addPermissionsForEmailEpic = (action$: Observable<ReduxAction>) =>
    action$.pipe(
      ofType(`CREATE_${sharedResourceType}_SHAREE`),
      mergeMap(({ payload }) => {
        return apiObservable(
          'POST',
          `/network/${resourcePath}/${payload.sharedResourceID}/share/`,
          {
            email: payload.email,
            permissions: [payload.permission],
          },
        ).pipe(
          map((response) =>
            actionCreators.shareeActions['createSucceeded']({
              ...response,
              email: payload.email,
              sharedResourceID: payload.sharedResourceID,
            }),
          ),
          startWith(actionCreators.shareeActions['createRequested'](payload)),
          catchError((err) => of(actionCreators.shareeActions['createFailed'](err))),
        );
      }),
    );

  const removePermissionForVerifiedEmailEpic = (action$: Observable<ReduxAction>) =>
    action$.pipe(
      ofType(`DELETE_VERIFIED_${sharedResourceType}_SHAREE_PERMISSION`),
      mergeMap(({ payload }) => {
        return apiObservable(
          'DELETE',
          `/${resourcePath}/share/${payload.permissionID}/`,
          undefined,
          {
            apiVersion: 'v2',
          },
        ).pipe(
          map((response) =>
            actionCreators.verifiedShareePermissionActions['deleteSucceeded']({
              ...response,
              removeAllAccess: payload.removeAllAccess,
            }),
          ),
          startWith(actionCreators.verifiedShareePermissionActions['deleteRequested'](payload)),
          catchError((err) =>
            of(actionCreators.verifiedShareePermissionActions['deleteFailed'](err)),
          ),
        );
      }),
    );

  const removePermissionForWaitingEmailEpic = (action$: Observable<ReduxAction>) =>
    action$.pipe(
      ofType(`DELETE_WAITING_${sharedResourceType}_SHAREE_PERMISSION`),
      mergeMap(({ payload }) => {
        return apiObservable('DELETE', `/share-remove/${payload.permissionID}/`, undefined, {
          apiVersion: 'v2',
        }).pipe(
          map((response) =>
            actionCreators.waitingShareePermissionActions['deleteSucceeded']({
              ...response,
              removeAllAccess: payload.removeAllAccess,
            }),
          ),
          startWith(actionCreators.waitingShareePermissionActions['deleteRequested'](payload)),
          catchError((err) =>
            of(actionCreators.waitingShareePermissionActions['deleteFailed'](err)),
          ),
        );
      }),
    );

  const publicSharingEpic = (action$: Observable<ReduxAction>) =>
    action$.pipe(
      ofType(`UPDATE_PUBLIC_${sharedResourceType}_SHARING`),
      mergeMap(({ payload }) => {
        return apiObservable(
          'PUT',
          `/network/${resourcePath}/${payload.shareStatus.id}/share-public/`,
          {
            shared: payload.shareStatus.publiclyShared,
            permissions: payload.shareStatus.publicPermissions,
          },
        ).pipe(
          map((response) =>
            actionCreators.publicSharingActions['updateSucceeded']({
              ...response,
              shareStatus: payload.shareStatus,
            }),
          ),
          startWith(actionCreators.publicSharingActions['updateRequested'](payload)),
          catchError((err) => of(actionCreators.publicSharingActions['updateFailed'](err))),
        );
      }),
    );

  return [
    fetchShareStatusEpic,
    addPermissionsForEmailEpic,
    removePermissionForVerifiedEmailEpic,
    removePermissionForWaitingEmailEpic,
    publicSharingEpic,
  ];
}
